import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageSquare, X, Send, RefreshCw } from 'lucide-react';
import { botKnowledgeBase } from './botKnowledge';
import logo from '../assets/logo192.png'; // Importar el logo
import '../styles/Chatbot.css';



const Chatbot = () => {
    const [isOpen, setIsOpen] = useState(false);
    // --- MODIFICADO: Inicializar el estado desde localStorage ---
    const [messages, setMessages] = useState(() => {
        try {
            const savedMessages = localStorage.getItem('chatbot_messages');
            // Si hay mensajes guardados, los parseamos. Si no, empezamos con un array vacío.
            return savedMessages ? JSON.parse(savedMessages) : [];
        } catch (error) {
            console.error("Error al leer los mensajes del chatbot desde localStorage:", error);
            // En caso de error (ej. JSON malformado), empezamos de cero.
            return [];
        }
    });
    const [inputValue, setInputValue] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const messagesEndRef = useRef(null);

    // Función para añadir un nuevo mensaje del bot
    const addBotMessage = useCallback((responseNode) => {
        setIsTyping(true);
        setTimeout(() => {
            setMessages(prev => [...prev, { 
                text: responseNode.text, 
                sender: 'bot', 
                options: responseNode.replies,
                timestamp: new Date().toISOString()
            }]);
            setIsTyping(false);

        }, 800); // Delay más corto para una sensación más rápida
    }, []);

    // Efecto para el mensaje de bienvenida
    useEffect(() => {
        if (isOpen && messages.length === 0) {
            addBotMessage(botKnowledgeBase.initial);
        }
    }, [isOpen, messages.length, addBotMessage]);

    // Efecto para hacer scroll hacia abajo
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, isTyping]);

    // --- NUEVO: Efecto para guardar los mensajes en localStorage ---
    useEffect(() => {
        try {
            // Guardamos el historial de mensajes cada vez que cambia.
            localStorage.setItem('chatbot_messages', JSON.stringify(messages));
        } catch (error) {
            console.error("Error al guardar los mensajes del chatbot en localStorage:", error);
        }
    }, [messages]);

    // --- NUEVO: Lógica de búsqueda por palabras clave ---
    const findResponseByKeywords = useCallback((userInput) => {
        const normalizedInput = userInput.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        
        // Buscar una coincidencia de palabra clave
        for (const key in botKnowledgeBase) {
            if (botKnowledgeBase[key].keywords) {
                const match = botKnowledgeBase[key].keywords.some(kw => normalizedInput.includes(kw));
                if (match) {
                    return botKnowledgeBase[key];
                }
            }
        }
        // Si no hay coincidencias, devolver la respuesta por defecto
        return botKnowledgeBase.default;
    }, []);

    // Manejador principal para la entrada del usuario (texto o respuesta rápida)
    const handleUserInput = useCallback((userInput) => {
        const responseKey = userInput.trim();
        if (!responseKey) return;

        // Manejar "Volver al inicio"
        if (responseKey === 'Volver al inicio') {
            addBotMessage(botKnowledgeBase.initial);
            return;
        }

        const responseNode = botKnowledgeBase[responseKey] || botKnowledgeBase.default;

        // Añadir el mensaje del usuario al historial
        // Si es una respuesta rápida, usa el userText predefinido para un look más limpio
        const userTextToShow = responseNode.userText || userInput;
        setMessages(prev => [...prev, { text: userTextToShow, sender: 'user', timestamp: new Date().toISOString() }]);
        
        // Añadir la respuesta del bot
        addBotMessage(responseNode);
    }, [addBotMessage]);

    const handleFormSubmit = useCallback((e) => {
        e.preventDefault();
        const userMessage = inputValue.trim();
        if (!userMessage) return;

        setMessages(prev => [...prev, { text: userMessage, sender: 'user', timestamp: new Date().toISOString() }]);
        setInputValue('');
        // --- MODIFICADO: Usar la búsqueda por palabras clave ---
        const responseNode = findResponseByKeywords(userMessage);
        addBotMessage(responseNode);
    }, [inputValue, addBotMessage, findResponseByKeywords]);

    const handleQuickReply = useCallback((reply) => {
        handleUserInput(reply);
    }, [handleUserInput]);

    // --- NUEVO: Función para borrar el historial ---
    const handleClearHistory = useCallback(() => {
        // Limpia el estado de los mensajes
        setMessages([]);
        // Limpia el historial guardado en localStorage
        localStorage.removeItem('chatbot_messages');
    }, []);

    // --- NUEVO: Función para formatear la hora ---
    const formatTimestamp = useCallback((isoString) => {
        const date = new Date(isoString);
        return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    }, []);

    // --- NUEVO: Función para obtener el texto del separador de fecha ---
    const getFormattedDateSeparator = useCallback((isoString) => {
        const date = new Date(isoString);
        const today = new Date();
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);

        // Reseteamos la hora para comparar solo las fechas
        date.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        yesterday.setHours(0, 0, 0, 0);

        if (date.getTime() === today.getTime()) {
            return 'Hoy';
        }
        if (date.getTime() === yesterday.getTime()) {
            return 'Ayer';
        }
        return date.toLocaleDateString('es-ES');
    }, []);

    return (
        <>
            <button className="chatbot-toggle-btn" onClick={() => setIsOpen(!isOpen)} aria-label="Abrir chat">
                {isOpen ? <X size={24} /> : <MessageSquare size={24} />}
            </button>

            {isOpen && (
                <div className="chatbot-window">
                    <div className="chatbot-header">
                        <div className="chatbot-avatar header-avatar"><img src={logo} alt="Lignums" /></div>
                        <div className="chatbot-title">
                            <span>Asistente Virtual </span>
                            <small> En línea</small>
                        </div>
                        <button className="chatbot-clear-btn" onClick={handleClearHistory} title="Reiniciar conversación">
                            <RefreshCw size={18} />
                        </button>
                        <button className="chatbot-close-btn" onClick={() => setIsOpen(false)}><X size={20} /></button>
                    </div>

                    <div className="chatbot-messages">
                        {messages.map((msg, index) => {
                            let dateSeparator = null;
                            const currentDate = new Date(msg.timestamp);

                            // Si es el primer mensaje o si la fecha es diferente al mensaje anterior
                            if (index === 0 || new Date(messages[index - 1].timestamp).toDateString() !== currentDate.toDateString()) {
                                dateSeparator = (
                                    <div className="date-separator">
                                        <span>{getFormattedDateSeparator(msg.timestamp)}</span>
                                    </div>
                                );
                            }

                            return (
                                <React.Fragment key={index}>
                                    {dateSeparator}
                                    <div className={`message-wrapper ${msg.sender}`}>
                                        {msg.sender === 'bot' && <div className="chatbot-avatar bot-avatar"><img src={logo} alt="Bot" /></div>}
                                        <div className="message-content">
                                            <div className={`chatbot-message ${msg.sender}-bubble`}>{msg.text}</div>
                                            {msg.timestamp && <span className="message-timestamp">{formatTimestamp(msg.timestamp)}</span>}
                                        </div>
                                    </div>
                                </React.Fragment>
                            );
                        })}
                        {isTyping && (
                            <div className="message-wrapper bot typing-wrapper">
                                <div className="chatbot-avatar bot-avatar"><img src={logo} alt="Bot" /></div>
                                <div className="typing-indicator">
                                    <span></span><span></span><span></span>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {messages.length > 0 && messages[messages.length - 1].sender === 'bot' && messages[messages.length - 1].options?.length > 0 && (
                        <div className="quick-replies-container">
                            {messages[messages.length - 1].options.map((option, i) => (
                                <button key={i} className="quick-reply-btn" onClick={() => handleQuickReply(option)}>
                                    {option}
                                </button>
                            ))}
                        </div>
                    )}

                    <form className="chatbot-input-form" onSubmit={handleFormSubmit}>
                        <input
                            className="chatbot-input"
                            type="text"
                            placeholder="Escribe tu mensaje..."
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            disabled={isTyping}
                        />
                        <button className="chatbot-send-btn" type="submit" disabled={isTyping || !inputValue.trim()} aria-label="Enviar mensaje">
                            <Send size={20} />
                        </button>
                    </form>
                </div>
            )}
        </>
    );
};

export default Chatbot;